
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Article } from '@/integrations/supabase/customTypes';

interface DashboardContextProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  dashboardData: {
    articlesCount: number;
    draftCount: number;
    publishedCount: number;
    totalViews: number;
    totalSubscribers: number;
    totalEarnings: number;
  };
  articles: Article[];
  stats: {
    total: number;
    published: number;
    drafts: number;
    totalViews: number;
  };
  refreshDashboardData: () => Promise<void>;
}

const DashboardContext = createContext<DashboardContextProps | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { userProfile } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    articlesCount: 0,
    draftCount: 0,
    publishedCount: 0,
    totalViews: 0,
    totalSubscribers: 0,
    totalEarnings: 0,
  });
  const [articles, setArticles] = useState<Article[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    drafts: 0,
    totalViews: 0
  });

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const refreshDashboardData = async () => {
    if (!userProfile) return;
    
    try {
      const { data: articlesData, error } = await supabase
        .from('articles')
        .select('*')
        .eq('author_id', userProfile.id);
      
      if (error) {
        console.error('Error fetching dashboard data:', error);
        return;
      }

      setArticles(articlesData || []);
      
      const articlesCount = articlesData?.length || 0;
      const draftCount = articlesData?.filter(a => a.status === 'draft').length || 0;
      const publishedCount = articlesData?.filter(a => a.status === 'published').length || 0;
      
      const totalViews = articlesData?.reduce((sum, article) => sum + (article.view_count || 0), 0) || 0;
      
      const totalSubscribers = 45;
      const totalEarnings = 345.75;
      
      setDashboardData({
        articlesCount,
        draftCount,
        publishedCount,
        totalViews,
        totalSubscribers,
        totalEarnings,
      });

      // Update stats for the overview component
      setStats({
        total: articlesCount,
        published: publishedCount,
        drafts: draftCount,
        totalViews
      });
    } catch (error) {
      console.error('Error in refreshDashboardData:', error);
    }
  };

  const aggregateArticleStats = (articles: Article[]) => {
    return articles.reduce(
      (stats, article) => {
        stats.total++;
        if (article.status === 'published') {
          stats.published++;
        } else if (article.status === 'draft') {
          stats.drafts++;
        }
        
        stats.totalViews += article.view_count || 0;
        
        return stats;
      },
      { total: 0, published: 0, drafts: 0, totalViews: 0 }
    );
  };

  useEffect(() => {
    if (userProfile) {
      refreshDashboardData();
    }
  }, [userProfile]);

  return (
    <DashboardContext.Provider
      value={{
        sidebarOpen,
        toggleSidebar,
        dashboardData,
        articles,
        stats,
        refreshDashboardData,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
